import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLocalePath, useT } from "../../i18n";
import { useApi } from "../../lib/api";
import { SectionHeading } from "../SectionHeading";
import { TextLink } from "../TextLink";
import { Reveal } from "../Reveal";
import { VehicleCard, VehicleCardSkeleton } from "../VehicleCard";
import { StateMessage } from "./StateMessage";

const CELL = "w-[80vw] shrink-0 snap-start sm:w-[46vw] md:w-auto";

export const LatestInventory = ({ index = "02" }) => {
  const t = useT();
  const lp = useLocalePath();
  const { data, loading, error } = useApi("/vehicles", { latest: true, limit: 6 });

  return (
    <section data-testid="latest-section" className="bg-paper py-24 text-ink sm:py-32 lg:py-40">
      <div className="container-x">
        <SectionHeading
          index={index}
          eyebrow={t("latest.eyebrow")}
          title={t("latest.title")}
          testId="latest-heading"
          action={<TextLink to={lp("inventory")} testId="latest-view-all" className="hidden md:inline-flex">{t("ui.viewAll")}</TextLink>}
        />
        {!loading && (error || !data?.items?.length) && <div className="mt-12"><StateMessage error={error} /></div>}
        <div
          data-testid="latest-grid"
          className="no-scrollbar -mx-5 mt-14 flex snap-x snap-mandatory scroll-px-5 gap-5 sm:scroll-px-8 overflow-x-auto px-5 sm:-mx-8 sm:px-8 md:mx-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-16 md:overflow-visible md:px-0 lg:mt-20 lg:grid-cols-3 lg:gap-x-10"
        >
          {loading && [0, 1, 2].map((i) => <div key={i} className={CELL}><VehicleCardSkeleton tone="light" /></div>)}
          {data?.items?.slice(0, 5).map((v, i) => (
            <div key={v.id} className={CELL}>
              <Reveal delay={(i % 3) * 0.1}>
                <VehicleCard vehicle={v} tone="light" sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 80vw" />
              </Reveal>
            </div>
          ))}
          {data?.items?.length > 0 && (
            <Link
              to={lp("inventory")}
              data-testid="latest-cta-tile"
              className="group hidden aspect-[3/2] flex-col justify-between border border-ink/15 p-8 transition-colors duration-500 hover:bg-ink hover:text-white md:flex lg:p-10"
            >
              <span className="eyebrow opacity-55">{t("latest.eyebrow")}</span>
              <span className="flex items-end justify-between gap-6">
                <span className="font-display text-3xl leading-tight lg:text-4xl">{t("ui.viewAll")}</span>
                <ArrowRight size={22} strokeWidth={1} className="shrink-0 transition-transform duration-500 group-hover:translate-x-1.5" />
              </span>
            </Link>
          )}
        </div>
        <TextLink to={lp("inventory")} testId="latest-view-all-mobile" className="mt-10 md:hidden">{t("ui.viewAll")}</TextLink>
      </div>
    </section>
  );
};
